import React from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { StaticSRPHeader } from './StaticSRPHeader';
import { HitCompactView } from '../../../components/Hit/HitCompactView';
import { buildHitCompactViewPropsFromStatic } from './buildHitCompactViewProps';
import type { StaticSRPProps } from '../../../types/srp-static';
import type { Locale } from '../../../utils/routing/generate-url';
import { generateUrlFromIndexUiState } from '../../../utils/algolia/url-handling';
import { getIndexName } from '../../../constants';
import Footer from '../../../components/Footer';
import { TopEmployersStripWithClientFetch } from '../../../components/TopEmployersStripWithClientFetch';
import { EngagementStrip } from '../../../components/engagement/EngagementStrip';
import { SrpResultsGridSkeleton } from '../../../components/search/SrpResultsGridSkeleton';
import { matchSearchIndexPath } from '../match-search-index-path';
import { getLocationFromFiltersAndRoute } from '../utils/seoUtils';

/** Number of job cards to fully render on the server; the rest are placeholders to reduce render+serialize time. */
const INITIAL_SSR_HITS = 12;

/**
 * Static SRP: no SearchProvider, FacetsProvider, or SavedState.
 * All data from props. Renders same layout as interactive SRP to avoid CLS.
 * When initialResults is null and useProgressiveSrp is true, shows a simple loading message (slim payload case).
 */
export function StaticSRPPage({ data, locale, pathname }: StaticSRPProps): React.ReactElement {
  const intl = useIntl();
  const results = data.initialResults;
  const hits = results?.hits ?? [];
  const nbHits = results?.nbHits ?? 0;
  const page = data.initialPage ?? 0;
  const nbPages = results?.nbPages ?? 1;
  const hasNoResults = (data.hasNoResults ?? false) || nbHits === 0;

  const isSlimPayload = !results && (data as { useProgressiveSrp?: boolean }).useProgressiveSrp === true;

  const localeTyped = locale as Locale;
  const filtersObj = typeof data.initialFilters === 'string'
    ? (() => { try { return JSON.parse(data.initialFilters) as Record<string, string[]>; } catch { return {}; } })()
    : (data.initialFilters ?? {});

  const matched = matchSearchIndexPath(pathname);
  const locationForStrip = getLocationFromFiltersAndRoute(filtersObj, {
    routeParams: matched ? matched.routeParams : {},
  });
  const hideTopEmployersStrip = Boolean(locationForStrip) || data.initialLayout === 'map';

  const initialHits = hits.slice(0, INITIAL_SSR_HITS);
  const placeholderCount = Math.max(0, hits.length - INITIAL_SSR_HITS);

  return (
    <div className="relative flex flex-col h-screen mobile-viewport-height overflow-hidden lg:overflow-visible">
      <StaticSRPHeader
        locale={locale}
        query={data.initialQuery ?? ''}
        facetsData={data.facetsData as Record<string, Array<{ value: string; label?: string; count: number; isRefined?: boolean }>> | undefined}
        refinementList={filtersObj}
        pathname={pathname}
        indexName={data.indexName ?? getIndexName(locale)}
      />
      <div className="flex-1 flex lg:flex-row lg:relative lg:overflow-hidden min-h-0">
        <div
          data-srp-scroll-region=""
          className="flex-1 bg-[#F8F5F0] dark:bg-[#170F07] overflow-x-hidden overflow-y-auto min-h-0 overscroll-y-contain touch-pan-y [-webkit-overflow-scrolling:touch] pt-1 sm:pt-1 md:pt-2 pb-20 lg:pb-0 sm:px-0 md:px-2 lg:px-8"
        >
          <div className="w-full bg-[#F8F5F0] dark:bg-[#170F07] pt-1 sm:pt-1 md:pt-2">
            <div className="max-w-screen-2xl mx-auto sm:px-0 md:px-2 lg:px-8">
              <TopEmployersStripWithClientFetch
                initialEmployers={hideTopEmployersStrip ? [] : data.topEmployersStrip}
                locale={locale}
                hideStrip={hideTopEmployersStrip}
              />
              {!isSlimPayload && !hasNoResults ? (
                <div className="px-2 @md:px-4 pb-2">
                  <EngagementStrip
                    feedbackSource="srp_static_shell"
                    variant="compact"
                    feedbackMetadata={{
                      pathname,
                      query: data.initialQuery ?? '',
                      nbHits,
                    }}
                  />
                </div>
              ) : null}
              <div className="ais-InstantSearch @container w-full min-w-0 pt-1 sm:pt-1 md:pt-2 flex flex-col gap-2">
                {/* Sidebar: job count only */}
                <aside
                  id="search-controls"
                  className="search-controls text-[#1E1A14] dark:text-[#F2EADF] px-2 @sm:px-2 @md:px-4 pb-2 @sm:pb-2 @md:pb-4 flex flex-col gap-2 @md:gap-3"
                >
                  <p className="text-sm text-[#7A6E5F] dark:text-[#B8A48C]">
                    {isSlimPayload ? null : (
                      <FormattedMessage
                        id="search.jobCount"
                        defaultMessage="{count, number} jobs"
                        description="Number of job results"
                        values={{ count: nbHits }}
                      />
                    )}
                  </p>
                </aside>

                <main id="job-results" className="job-results">
                  <section
                    className="srp-results"
                    aria-labelledby="results-title"
                  >
                    <h2 id="results-title" className="sr-only">
                      <FormattedMessage id="search.results.title" defaultMessage="Job Search Results" description="Screen reader heading for the job search results section" />
                    </h2>
                    {isSlimPayload ? (
                      <SrpResultsGridSkeleton />
                    ) : hasNoResults ? (
                      <div className="py-6 space-y-4">
                        <p className="text-[#7A6E5F] dark:text-[#B8A48C]">
                          <FormattedMessage id="search.noResults" defaultMessage="No jobs found." description="No results message" />
                        </p>
                        <EngagementStrip
                          feedbackSource="srp_static_no_results"
                          feedbackMetadata={{
                            pathname,
                            query: data.initialQuery ?? '',
                            filters: filtersObj,
                          }}
                        />
                      </div>
                    ) : (
                      <ul className="srp-results-grid">
                        {initialHits.map((hit, index) => (
                          <li
                            key={(hit.objectID ?? hit.id) as string ?? index}
                            className="srp-results-grid-item"
                          >
                          <HitCompactView
                            {...buildHitCompactViewPropsFromStatic(
                              hit as Record<string, unknown>,
                              index,
                              data,
                              localeTyped,
                              pathname
                            )}
                          />
                        </li>
                      ))}
                      {Array.from({ length: placeholderCount }, (_, i) => (
                        <li
                          key={`placeholder-${initialHits.length + i}`}
                          className="srp-results-grid-item min-h-[150px]"
                          aria-hidden
                        />
                      ))}
                      </ul>
                    )}
                  </section>
                </main>

                {/* Minimal pagination: next/prev links (hidden when loading slim payload) */}
                {!isSlimPayload && nbPages > 1 && (() => {
                  const filtersObj = typeof data.initialFilters === 'string'
                    ? (() => { try { return JSON.parse(data.initialFilters) as Record<string, string[]>; } catch { return {}; } })()
                    : (data.initialFilters ?? {});
                  const prevUrl = page > 0
                    ? generateUrlFromIndexUiState(
                        { query: data.initialQuery ?? '', refinementList: filtersObj, page: page - 1 },
                        data.indexName ?? getIndexName(locale),
                        pathname
                      )
                    : null;
                  const nextUrl = page < nbPages - 1
                    ? generateUrlFromIndexUiState(
                        { query: data.initialQuery ?? '', refinementList: filtersObj, page: page + 1 },
                        data.indexName ?? getIndexName(locale),
                        pathname
                      )
                    : null;
                  return (
                    <nav className="flex items-center justify-center gap-4 py-2" aria-label="Pagination">
                      {prevUrl && (
                        <a href={prevUrl} className="text-sm font-medium text-[#3B5E45] dark:text-[#98CFA8] hover:underline">
                          ← Previous
                        </a>
                      )}
                      <span className="text-sm text-[#7A6E5F] dark:text-[#B8A48C]">
                        Page {page + 1} of {nbPages}
                      </span>
                      {nextUrl && (
                        <a href={nextUrl} className="text-sm font-medium text-[#3B5E45] dark:text-[#98CFA8] hover:underline">
                          Next →
                        </a>
                      )}
                    </nav>
                  );
                })()}
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </div>
      {/* Mobile bottom bar placeholder to reserve space */}
      <div className="lg:hidden h-14 flex-shrink-0 bg-[#F2EADF] dark:bg-[#2A1D0F]" aria-hidden />
    </div>
  );
}
